<template>
  <v-text-field
    ref="field"
    v-bind="inputAttrs"
    :value="value"
    @paste.native="handlePaste"
    v-on="forwardedListeners"
  />
</template>

<script>
// Change these values here to update the limit for every PriceField.
const MAX_INTEGER_DIGITS = 8;
const MAX_DECIMAL_PLACES = 2;
const MAX_PRICE = Number(
  `${"9".repeat(MAX_INTEGER_DIGITS)}.${"9".repeat(MAX_DECIMAL_PLACES)}`
);

export default {
  name: "PriceField",
  inheritAttrs: false,
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: [String, Number],
      default: null,
    },
  },
  computed: {
    inputAttrs() {
      return {
        // Text inputs preserve a trailing decimal point while the user types
        // and support selectionStart/selectionEnd used by the key handlers.
        type: "text",
        inputmode: "decimal",
        ...this.$attrs,
      };
    },
    forwardedListeners() {
      const listeners = { ...this.$listeners };
      delete listeners.paste;
      return {
        ...listeners,
        input: this.handleInput,
        keypress: this.handleKeypress,
        blur: this.handleBlur,
      };
    },
  },
  methods: {
    isValidPrice(value) {
      if (value === "") {
        return true;
      }

      const pattern = new RegExp(
        `^\\d{1,${MAX_INTEGER_DIGITS}}(?:\\.\\d{0,${MAX_DECIMAL_PLACES}})?$`
      );
      return pattern.test(value) && Number(value) <= MAX_PRICE;
    },
    handleInput(value) {
      const inputValue = value == null ? "" : String(value);
      if (this.isValidPrice(inputValue)) {
        this.$emit("input", value);
        return;
      }

      this.$nextTick(() => {
        const input =
          this.$refs.field && this.$refs.field.$el.querySelector("input");
        if (input) {
          input.value = this.value == null ? "" : this.value;
        }
      });
    },
    handleKeypress(event) {
      const key = event.key;
      const input = event.target;
      const currentValue = input.value || "";

      if (key && key.length === 1 && /[0-9.]/.test(key)) {
        const start =
          input.selectionStart == null
            ? currentValue.length
            : input.selectionStart;
        const end = input.selectionEnd == null ? start : input.selectionEnd;
        const nextValue = `${currentValue.slice(0, start)}${key}${currentValue.slice(end)}`;
        if (!this.isValidPrice(nextValue)) {
          event.preventDefault();
        }
      }

      this.$emit("keypress", event);
    },
    handlePaste(event) {
      const clipboard = event.clipboardData || window.clipboardData;
      const pastedValue = clipboard ? clipboard.getData("text") : "";
      const input = event.target;
      const currentValue = input.value || "";
      const start =
        input.selectionStart == null
          ? currentValue.length
          : input.selectionStart;
      const end = input.selectionEnd == null ? start : input.selectionEnd;
      const rawValue = `${currentValue.slice(0, start)}${pastedValue}${currentValue.slice(end)}`;

      if (
        rawValue !== "" &&
        !/^\d+(?:\.\d*)?$/.test(rawValue)
      ) {
        event.preventDefault();
        this.$emit("paste", event);
        return;
      }

      const [integerPart, decimalPart] = rawValue.split(".");
      const normalizedIntegerPart = (integerPart || "").slice(
        0,
        MAX_INTEGER_DIGITS
      );
      const normalizedValue =
        decimalPart === undefined
          ? normalizedIntegerPart
          : `${normalizedIntegerPart}.${decimalPart.slice(0, MAX_DECIMAL_PLACES)}`;

      if (normalizedValue !== rawValue) {
        event.preventDefault();
        this.$emit("input", normalizedValue);
        this.$nextTick(() => {
          if (input) {
            input.value = normalizedValue;
          }
        });
      }

      this.$emit("paste", event);
    },
    handleBlur(event) {
      const inputValue = this.value == null ? "" : String(this.value);
      if (inputValue.endsWith(".")) {
        this.$emit("input", inputValue.slice(0, -1));
      }

      this.$emit("blur", event);
    },
  },
};
</script>
